import type { InvoiceFormData, LineItem, PaymentMethod } from './types'

/**
 * Helper function to get the correct invoice status from salesSettings
 */
function getInvoiceStatus(apiInvoice: any, salesSettings?: any[]): string {
  // If API has sales_state, try to find matching salesSetting
  if (apiInvoice.sales_state && salesSettings) {
    // Try to find by name match
    const setting = salesSettings.find(s => 
      s.cash_name_ar === apiInvoice.sales_state || 
      s.agel_name_ar === apiInvoice.sales_state ||
      s.name_a === apiInvoice.sales_state
    )
    if (setting) {
      return setting.id.toString()
    }
  }

  // If no sales_state or no match, find the first appropriate default
  if (salesSettings && salesSettings.length > 0) {
    const paymentType = apiInvoice.payment_type?.toString() || '1'
    
    if (paymentType === '1') {
      // Cash payment - find first visible cash option
      const cashSetting = salesSettings.find(s => 
        s.is_cash_visible === 1 && s.cash_name_ar !== '-------------'
      )
      if (cashSetting) {
        return cashSetting.id.toString()
      }
    } else {
      // Deferred payment - find first visible agel option
      const agelSetting = salesSettings.find(s => 
        s.is_agel_visible === 1 && s.agel_name_ar !== '-------------'
      )
      if (agelSetting) {
        return agelSetting.id.toString()
      }
    }
  }

  return '' // Fallback to empty
}

/**
 * Transform API invoice response to form data structure
 */
export function transformApiInvoiceToFormData(
  apiInvoice: any,
  branches?: any[],
  accountGuideMap?: Record<string, any>,
  paymentMethodsArray?: any[],
  defaults?: any,
  salesSettings?: any[]
): InvoiceFormData {
  console.log('=== TRANSFORM API INVOICE TO FORM DATA ===')
  console.log('Input apiInvoice:', apiInvoice)
  console.log('Branches for lookup:', branches)
  console.log('PaymentMethods for mapping:', paymentMethodsArray)
  console.log('AccountGuideMap available for customer lookup:', !!accountGuideMap)
  
  // Find branch name using branch_code_id
  const branch = branches?.find(b => b.id === apiInvoice.branch_code_id)
  const branchName = branch?.name_a_original || branch?.name_a || ''
  console.log('Found branch:', branch)
  console.log('Branch name:', branchName)
  
  // Extract line items - in this case, each invoice record represents one line item
  const lineItem: LineItem = {
    id: apiInvoice.id?.toString() || Date.now().toString(),
    product_code: apiInvoice.product_code || '',
    product_name: apiInvoice.product_name || '',
    serial_number: apiInvoice.insertion_num || '',
    quantity: parseFloat(apiInvoice.quantity || '0'),
    carat: parseFloat(apiInvoice.carat || '0'),
    unit: apiInvoice.unit || '',
    weight: parseFloat(apiInvoice.stone_weight || '0'),
    stone_weight: parseFloat(apiInvoice.stone_weight || '0'),
    unit_price: parseFloat(apiInvoice.selling_price || '0'),
    salary: parseFloat(apiInvoice.salary || '0'),
    total_salary: parseFloat(apiInvoice.total_salaries || '0'),
    tax_rate: parseFloat(apiInvoice.vat || '0'),
    discount: parseFloat(apiInvoice.discount || '0'),
    total: parseFloat(apiInvoice.total || '0')
  }

  // Extract payment methods from payment_1_*, payment_2_*, etc. fields
  const paymentMethods: PaymentMethod[] = []
  
  // Create mapping from API payment method IDs to the actual UI values
  let paymentMethodMapping: Record<string, string> = {}
  
  if (paymentMethodsArray && Array.isArray(paymentMethodsArray)) {
    // Use actual payment methods from API
    console.log('Using actual payment methods from API:', paymentMethodsArray)
    paymentMethodsArray.forEach((method, index) => {
      if (typeof method === 'string') {
        paymentMethodMapping[index.toString()] = method
      } else if (method && method.id && method.name) {
        paymentMethodMapping[method.id.toString()] = method.name
      }
    })
  } 
  
  console.log('Final payment method mapping:', paymentMethodMapping)
  
  for (let i = 1; i <= 5; i++) {
    const amount = apiInvoice[`payment_${i}_amount`]
    const method = apiInvoice[`payment_${i}_payment_method`]
    const currencyId = apiInvoice[`payment_${i}_currency_id`]
    const reference = apiInvoice[`payment_${i}_reference`]
    const accountGuideId = apiInvoice[`payment_${i}_account_guide_id`]
    
    if (amount && parseFloat(amount) > 0) {
      paymentMethods.push({
        id: `payment-${i}`,
        method: paymentMethodMapping[method?.toString()] || '',
        amount: parseFloat(amount),
        currency: currencyId?.toString() || '',
        reference: reference || '',
        currencyId: currencyId?.toString() || '',
        accountGuideId: accountGuideId?.toString() || ''
      })
    }
  }

  // If no payment methods found, add empty one
  if (paymentMethods.length === 0) {
    paymentMethods.push({
      id: 'payment-1',
      method: '',
      amount: 0,
      currency: '1', // Default to currency ID 1 (SAR)
      reference: '',
      currencyId: '1',
      accountGuideId: ''
    })
  }

  // Derive customer data
  const parentAccountId = apiInvoice.parent_account_guide_id
  const customerAccount = parentAccountId && accountGuideMap ? accountGuideMap[String(parentAccountId)] : null
  const customerCode = customerAccount?.total_code || customerAccount?.code || apiInvoice.supplier_code || ''
  const customerName = apiInvoice.client_name || apiInvoice.supplier_name || customerAccount?.name_a_original || customerAccount?.name_a || ''

  // Transform main invoice data
  const formData: InvoiceFormData = {
    invoice_number: apiInvoice.bond_num || apiInvoice.kayed_num || '',
    bond_num: apiInvoice.bond_num || '',
    invoice_date: apiInvoice.bond_date ? apiInvoice.bond_date.replace(/\//g, '-') : '',
    def_branch: apiInvoice.branch_code_id || 0,
    staff_user_id: apiInvoice.staff_user_id || 0,
    branch: branchName,
    salesperson: apiInvoice.staff?.name_ar || defaults?.seller || '',
    payment_type: apiInvoice.payment_type?.toString() || '',
    invoice_status: getInvoiceStatus(apiInvoice, salesSettings),
    payment_method: apiInvoice.payment_method_sales?.toString() || '',
    document_number: apiInvoice.bond_num || apiInvoice.voucher_num || '',
    customer_id: apiInvoice.parent_account_guide_id?.toString() || '',
    customer_code: customerCode,
    customer_name: customerName,
    customer_phone: apiInvoice.customer_phone || '',
    customer_email: '', // Not available in API response
    identity_type: apiInvoice.identity_type || '',
    identity_number: apiInvoice.tax_or_identity_number || '',
    tax_number: apiInvoice.tax_or_identity_number || '',
    statement: apiInvoice.sales_state || 'سداد نقداً بالكامل', // Use sales_state or default statement
    seller: apiInvoice.staff?.name_ar || '',
    timing: apiInvoice.delivery_hour || '',
    currency: paymentMethods[0]?.currencyId?.toString() || '1', // Default to currency ID 1 (SAR)
    exchange_rate: 1, // Default to 1 if not available
    notes: '', // Not available in API response
    line_items: [lineItem],
    payment_methods: paymentMethods,
    delivery_date: apiInvoice.delivery_date || '',
    delivery_time: apiInvoice.delivery_hour || '',
    delivery_address: apiInvoice.delivery_address || apiInvoice.general_delivery_address || '',
    zatca_qr_code: '', // Not available in API response
    zatca_uuid: apiInvoice.uuid || '',
    zatca_hash: apiInvoice.invoice_hash || '',
    zatca_reported: Boolean(apiInvoice.zatca_reported)
  }

  console.log('✅ Transformation completed, returning formData:', formData)
  console.log('🔍 Key field mappings:')
  console.log('  - bond_num (رقم السند) API:', apiInvoice.bond_num, '→ invoice_number (form):', formData.invoice_number)
  console.log('  - bond_date (تاريخ السند) API:', apiInvoice.bond_date, '→ invoice_date (form):', formData.invoice_date)  
  console.log('  - sales_state (حالة الفاتورة) API:', apiInvoice.sales_state, '→ invoice_status (form):', formData.invoice_status)
  console.log('  - voucher_num API:', apiInvoice.voucher_num, '→ document_number (form):', formData.document_number)
  console.log('  - payment_type (نوع الدفع) API:', apiInvoice.payment_type, '→ payment_type (form):', formData.payment_type)
  console.log('  - branch_code_id (الفرع) API:', apiInvoice.branch_code_id, '→ def_branch (form):', formData.def_branch)
  console.log('  - product_code (كود المنتج) API:', apiInvoice.product_code, '→ line_items[0].product_code (form):', formData.line_items[0]?.product_code)
  console.log('  - product_name (اسم المنتج) API:', apiInvoice.product_name, '→ line_items[0].product_name (form):', formData.line_items[0]?.product_name)
  
  return formData
}

/**
 * Transform multiple invoice records that belong to the same bond_num into a single form data
 * This handles cases where multiple line items exist for the same invoice
 */
export function transformMultipleApiInvoicesToFormData(
  apiInvoices: any[],
  branches?: any[],
  accountGuideMap?: Record<string, any>,
  paymentMethodsArray?: any[],
  defaults?: any,
  salesSettings?: any[]
): InvoiceFormData | null {
  if (!apiInvoices || apiInvoices.length === 0) {
    return null
  }

  // Use the first invoice as the main invoice data and combine other line items
  const mainInvoice = apiInvoices[0]
  const formData = transformApiInvoiceToFormData(mainInvoice, branches, accountGuideMap, paymentMethodsArray, defaults, salesSettings)

  // If there are multiple records, they represent multiple line items for the same invoice
  if (apiInvoices.length > 1) {
    const lineItems: LineItem[] = apiInvoices.map((invoice, index) => ({
      id: invoice.id?.toString() || `${Date.now()}-${index}`,
      product_code: invoice.product_code || '',
      product_name: invoice.product_name || '',
      serial_number: invoice.insertion_num || '',
      quantity: parseFloat(invoice.quantity || '0'),
      carat: parseFloat(invoice.carat || '0'),
      unit: invoice.unit || '',
      weight: parseFloat(invoice.stone_weight || '0'),
      stone_weight: parseFloat(invoice.stone_weight || '0'),
      unit_price: parseFloat(invoice.selling_price || '0'),
      salary: parseFloat(invoice.salary || '0'),
      total_salary: parseFloat(invoice.total_salaries || '0'),
      tax_rate: parseFloat(invoice.vat || '0'),
      discount: parseFloat(invoice.discount || '0'),
      total: parseFloat(invoice.total || '0')
    }))

    formData.line_items = lineItems
  }

  return formData
}
